/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        CAT Translation Projects List & Management View (Task 10.5.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  FileText,
  Code,
  Plus,
  Search,
  Download,
  Trash2,
  CheckCircle2,
  Clock,
  ArrowRight,
  FolderOpen,
  Filter,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useCatProjectsStore,
  CatProjectItem,
  CatProjectStatus,
} from '../../stores/useCatProjectsStore';
import { NewProjectModal } from './NewProjectModal';

interface CatProjectsListProps {
  onOpenProject: (projectId: string) => void;
  onExportProject: (projectId: string) => void;
}

export const CatProjectsList: React.FC<CatProjectsListProps> = ({
  onOpenProject,
  onExportProject,
}) => {
  const { t } = useTranslation();
  const {
    searchQuery,
    statusFilter,
    languageFilter,
    setSearchQuery,
    setStatusFilter,
    setLanguageFilter,
    getFilteredProjects,
    getProjectStatsSummary,
    deleteProject,
    loadProjectToEditor,
  } = useCatProjectsStore();

  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<CatProjectItem | null>(null);

  const filteredProjects = getFilteredProjects();
  const summary = getProjectStatsSummary();

  const handleOpen = (project: CatProjectItem) => {
    loadProjectToEditor(project.id);
    onOpenProject(project.id);
  };

  const handleExport = (project: CatProjectItem) => {
    loadProjectToEditor(project.id);
    onExportProject(project.id);
  };

  const confirmDelete = () => {
    if (projectToDelete) {
      deleteProject(projectToDelete.id);
      setProjectToDelete(null);
    }
  };

  const getStatusBadge = (status: CatProjectStatus) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
            <CheckCircle2 className="size-3" />
            {t('cat.statusCompleted')}
          </span>
        );
      case 'review':
        return (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20">
            <Clock className="size-3" />
            {t('cat.statusReview')}
          </span>
        );
      case 'in_progress':
      default:
        return (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
            <Clock className="size-3" />
            {t('cat.statusInProgress')}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Metrics Summary Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="p-4 rounded-xl border border-surface-border bg-surface shadow-sm">
          <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider">
            {t('cat.statTotalProjects')}
          </p>
          <p className="text-2xl font-bold font-heading text-foreground mt-1">
            {summary.totalProjects}
          </p>
        </div>

        <div className="p-4 rounded-xl border border-surface-border bg-surface shadow-sm">
          <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider">
            {t('cat.statTotalWords')}
          </p>
          <p className="text-2xl font-bold font-heading text-foreground mt-1">
            {summary.totalWords.toLocaleString()}
          </p>
        </div>

        <div className="p-4 rounded-xl border border-surface-border bg-surface shadow-sm">
          <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider">
            {t('cat.statAvgProgress')}
          </p>
          <p className="text-2xl font-bold font-heading text-primary mt-1">
            {summary.avgProgress}%
          </p>
        </div>

        <div className="p-4 rounded-xl border border-surface-border bg-surface shadow-sm">
          <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider">
            {t('cat.statCompletedProjects')}
          </p>
          <p className="text-2xl font-bold font-heading text-emerald-500 mt-1">
            {summary.completedCount}
          </p>
        </div>
      </div>

      {/* Control & Filter Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-3 rounded-xl border border-surface-border bg-surface shadow-sm">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-foreground-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('cat.searchProjectsPlaceholder')}
            className="w-full pl-9 pr-3 py-1.5 text-xs rounded-lg border border-surface-border bg-surface-elevated/40 text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Status Filters */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0">
          {(['all', 'in_progress', 'completed', 'review'] as const).map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
                statusFilter === status
                  ? 'bg-primary text-primary-fg font-semibold shadow-xs'
                  : 'text-foreground-muted hover:text-foreground hover:bg-surface-elevated'
              }`}
            >
              {status === 'all'
                ? t('cat.filterAllProjects')
                : status === 'in_progress'
                ? t('cat.filterInProgress')
                : status === 'completed'
                ? t('cat.filterCompleted')
                : t('cat.filterReview')}
            </button>
          ))}
        </div>

        {/* Language & Actions */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-surface-border bg-surface-elevated/40 text-xs text-foreground-muted">
            <Filter className="size-3.5" />
            <select
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
              className="bg-transparent text-xs text-foreground focus:outline-none cursor-pointer"
            >
              <option value="all">{t('cat.allLanguages')}</option>
              <option value="vi">VI (Vietnamese)</option>
              <option value="en">EN (English)</option>
              <option value="ja">JA (Japanese)</option>
              <option value="zh">ZH (Chinese)</option>
            </select>
          </div>

          <button
            type="button"
            onClick={() => setIsNewProjectModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-fg hover:bg-primary/90 shadow-sm transition-colors whitespace-nowrap"
          >
            <Plus className="size-4" />
            <span>{t('cat.newProjectBtn')}</span>
          </button>
        </div>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <div className="p-12 text-center rounded-xl border border-dashed border-surface-border bg-surface-elevated/20 space-y-3">
          <div className="size-12 mx-auto rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <FolderOpen className="size-6" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground">
              {t('cat.noProjectsFoundTitle')}
            </h3>
            <p className="text-xs text-foreground-muted mt-1 max-w-sm mx-auto">
              {t('cat.noProjectsFoundDesc')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsNewProjectModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium rounded-lg bg-primary text-primary-fg hover:bg-primary/90 shadow-sm transition-colors"
          >
            <Plus className="size-3.5" />
            <span>{t('cat.createFirstProjectBtn')}</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredProjects.map((project) => {
            return (
              <div
                key={project.id}
                className="group p-5 rounded-xl border border-surface-border bg-surface hover:border-primary/40 hover:shadow-md transition-all flex flex-col justify-between space-y-4"
              >
                {/* Card Top: Icon, Title, Status */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                      {project.fileType === 'json' || project.fileType === 'xliff' ? (
                        <Code className="size-5" />
                      ) : (
                        <FileText className="size-5" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3
                        onClick={() => handleOpen(project)}
                        className="text-sm font-bold text-foreground truncate cursor-pointer hover:text-primary transition-colors"
                      >
                        {project.title}
                      </h3>
                      <div className="flex items-center gap-2 text-[11px] text-foreground-muted mt-0.5">
                        <span className="truncate">{project.fileName}</span>
                        <span>&bull;</span>
                        <span className="uppercase font-semibold px-1.5 py-0.2 rounded bg-surface-elevated text-foreground font-mono">
                          {project.fileType}
                        </span>
                        <span>&bull;</span>
                        <span>{(project.fileSizeBytes / 1024).toFixed(0)} KB</span>
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0">{getStatusBadge(project.status)}</div>
                </div>

                {/* Middle: Progress Bar and Word Count */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-muted">
                      {project.completedSegments} / {project.totalSegments} {t('cat.segmentsCountLabel')} ({project.totalWords.toLocaleString()} {t('cat.wordsCountLabel')})
                    </span>
                    <span className="font-bold text-foreground">
                      {project.progress}%
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-surface-elevated rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 rounded-full ${
                        project.progress === 100 ? 'bg-emerald-500' : 'bg-primary'
                      }`}
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                </div>

                {/* Bottom: Tags and Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-surface-border text-xs">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-md font-mono font-semibold bg-surface-elevated text-foreground-muted text-[11px]">
                      {project.sourceLang.toUpperCase()} &rarr; {project.targetLang.toUpperCase()}
                    </span>
                    <span className="text-[11px] text-foreground-muted hidden sm:inline">
                      {t('cat.byTranslator')}: <strong className="text-foreground font-medium">{project.translatorName}</strong>
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleExport(project)}
                      title={t('cat.exportDocument')}
                      className="p-1.5 text-foreground-muted hover:text-foreground rounded-lg hover:bg-surface-elevated transition-colors"
                    >
                      <Download className="size-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setProjectToDelete(project)}
                      title={t('cat.deleteProject')}
                      className="p-1.5 text-foreground-muted hover:text-rose-500 rounded-lg hover:bg-surface-elevated transition-colors"
                    >
                      <Trash2 className="size-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOpen(project)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-fg hover:bg-primary/90 transition-colors"
                    >
                      <span>{t('cat.openWorkbenchBtn')}</span>
                      <ArrowRight className="size-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Project Modal */}
      <NewProjectModal
        isOpen={isNewProjectModalOpen}
        onClose={() => setIsNewProjectModalOpen(false)}
        onProjectCreated={(id) => {
          setIsNewProjectModalOpen(false);
          onOpenProject(id);
        }}
      />

      {/* Delete Confirmation Dialog */}
      {projectToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-surface border border-surface-border rounded-xl shadow-2xl p-5 space-y-4">
            <h3 className="text-base font-bold text-foreground">
              {t('cat.deleteProjectTitle')}
            </h3>
            <p className="text-xs text-foreground-muted leading-relaxed">
              {t('cat.deleteProjectConfirmDesc', { name: projectToDelete.title })}
            </p>
            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setProjectToDelete(null)}
                className="px-4 py-2 text-xs font-medium rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
              >
                {t('cat.cancelBtn')}
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 text-xs font-medium rounded-lg bg-rose-600 text-white hover:bg-rose-700 shadow-sm transition-colors"
              >
                {t('cat.deleteConfirmBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
